// Multi-language support for Proof of Learning
export type Language = 'en' | 'es' | 'zh' | 'fr' | 'de';

export interface Translations {
  // Navigation
  nav: {
    feed: string;
    map: string;
    myShifts: string;
    trails: string;
    addShift: string;
    profile: string;
    signIn: string;
    signOut: string;
    search: string;
    lightMode: string;
    darkMode: string;
  };
  // Common
  common: {
    loading: string;
    error: string;
    retry: string;
    save: string;
    cancel: string;
    delete: string;
    edit: string;
    share: string;
    close: string;
    back: string;
    next: string;
    previous: string;
    viewAll: string;
    learnMore: string;
    copied: string;
    noResults: string;
    searchPlaceholder: string;
  };
  // Feed page
  feed: {
    title: string;
    subtitle: string;
    emptyState: string;
    filterAll: string;
    filterArticle: string;
    filterVideo: string;
    filterPaper: string;
    filterPodcast: string;
    viewShift: string;
    saveShift: string;
    views: string;
    readMore: string;
  };
  // Map page
  map: {
    title: string;
    subtitle: string;
    emptyState: string;
    clustering: string;
    fitView: string;
    viewDetails: string;
    shiftsInArea: string;
    location: string;
  };
  // My Shifts page
  myShifts: {
    title: string;
    subtitle: string;
    emptyState: string;
    emptyAction: string;
    public: string;
    private: string;
    createdAt: string;
    totalShifts: string;
    publicShifts: string;
  };
  // Trails page
  trails: {
    title: string;
    subtitle: string;
    emptyState: string;
    emptyAction: string;
    createTrail: string;
    trailName: string;
    trailDescription: string;
    addShifts: string;
    shifts: string;
    shareTrail: string;
  };
  // Create page
  create: {
    title: string;
    subtitle: string;
    step1: string;
    step2: string;
    step3: string;
    step4: string;
    sourceLink: string;
    sourceLinkPlaceholder: string;
    sourceTitle: string;
    sourceTitlePlaceholder: string;
    sourceType: string;
    beforeBelief: string;
    beforeBeliefPlaceholder: string;
    afterBelief: string;
    afterBeliefPlaceholder: string;
    reflection: string;
    reflectionPlaceholder: string;
    location: string;
    locationPlaceholder: string;
    visibility: string;
    visibilityPublic: string;
    visibilityPrivate: string;
    submit: string;
    submitting: string;
    success: string;
  };
  // Profile page
  profile: {
    title: string;
    subtitle: string;
    stats: string;
    totalShifts: string;
    publicShifts: string;
    savedShifts: string;
    totalViews: string;
    privacy: string;
    privacyPublic: string;
    privacyShareable: string;
    privacyPrivate: string;
    shareProfile: string;
    settings: string;
    language: string;
  };
  // Onboarding
  onboarding: {
    welcome: string;
    welcomeDesc: string;
    tagline: string;
    step1Title: string;
    step1Desc: string;
    step2Title: string;
    step2Desc: string;
    step3Title: string;
    step3Desc: string;
    step4Title: string;
    step4Desc: string;
    step5Title: string;
    step5Desc: string;
    skip: string;
    getStarted: string;
  };
  // Tooltips
  tooltips: {
    beforeBelief: string;
    afterBelief: string;
    reflection: string;
    publicVisibility: string;
    privateVisibility: string;
    clustering: string;
    fitView: string;
    shareProfile: string;
  };
}

export const translations: Record<Language, Translations> = {
  en: {
    nav: {
      feed: 'Learning Shifts',
      map: 'Concept Map',
      myShifts: 'My Proofs',
      trails: 'Reasoning Trail',
      addShift: 'Create Proof',
      profile: 'Learning Space',
      signIn: 'Sign In',
      signOut: 'Sign Out',
      search: 'Search...',
      lightMode: 'Light mode',
      darkMode: 'Dark mode',
    },
    common: {
      loading: 'Loading...',
      error: 'Something went wrong',
      retry: 'Try Again',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      share: 'Share',
      close: 'Close',
      back: 'Back',
      next: 'Next',
      previous: 'Previous',
      viewAll: 'View All',
      learnMore: 'Learn More',
      copied: 'Copied!',
      noResults: 'No results found',
      searchPlaceholder: 'Search shifts, trails...',
    },
    feed: {
      title: 'Learning Shifts',
      subtitle: 'Explore how learning becomes visible through shifts in understanding',
      emptyState: 'No proofs yet. Be the first to document your learning!',
      filterAll: 'All',
      filterArticle: 'Articles',
      filterVideo: 'Videos',
      filterPaper: 'Papers',
      filterPodcast: 'Podcasts',
      viewShift: 'View Shift',
      saveShift: 'Save',
      views: 'views',
      readMore: 'Read more',
    },
    map: {
      title: 'Concept Map',
      subtitle: 'Connections between ideas that show how concepts relate',
      emptyState: 'No location-based proofs yet',
      clustering: 'Clustering',
      fitView: 'Fit all',
      viewDetails: 'View Details',
      shiftsInArea: 'shifts in this area',
      location: 'Location',
    },
    myShifts: {
      title: 'My Shifts',
      subtitle: 'Your documented cognitive evolution',
      emptyState: 'You haven\'t documented any shifts yet.',
      emptyAction: 'Create your first shift',
      public: 'Public',
      private: 'Private',
      createdAt: 'Created',
      totalShifts: 'Total Shifts',
      publicShifts: 'Public',
    },
    trails: {
      title: 'Reasoning Trail',
      subtitle: 'A trace of how understanding develops over time',
      emptyState: 'No trails yet. Create your first reasoning trail!',
      emptyAction: 'Create Trail',
      createTrail: 'Create Trail',
      trailName: 'Trail Name',
      trailDescription: 'Description',
      addShifts: 'Add Shifts',
      shifts: 'shifts',
      shareTrail: 'Share Trail',
    },
    create: {
      title: 'Create Proof',
      subtitle: 'Capture a moment of cognitive evolution',
      step1: 'Source',
      step2: 'Before',
      step3: 'After',
      step4: 'Details',
      sourceLink: 'Source Link',
      sourceLinkPlaceholder: 'https://...',
      sourceTitle: 'Source Title',
      sourceTitlePlaceholder: 'Article, video, or paper title',
      sourceType: 'Source Type',
      beforeBelief: 'What I Believed Before',
      beforeBeliefPlaceholder: 'Describe your understanding before this source...',
      afterBelief: 'What I Understand Now',
      afterBeliefPlaceholder: 'Describe your new understanding...',
      reflection: 'Reflection',
      reflectionPlaceholder: 'What made this shift significant?',
      location: 'Location (optional)',
      locationPlaceholder: 'Where were you when you learned this?',
      visibility: 'Visibility',
      visibilityPublic: 'Public - Share with the community',
      visibilityPrivate: 'Private - Only visible to you',
      submit: 'Save Shift',
      submitting: 'Saving...',
      success: 'Shift documented successfully!',
    },
    profile: {
      title: 'Learning Space',
      subtitle: 'Your learning journey',
      stats: 'Statistics',
      totalShifts: 'Total Shifts',
      publicShifts: 'Public Shifts',
      savedShifts: 'Saved Shifts',
      totalViews: 'Total Views',
      privacy: 'Privacy Settings',
      privacyPublic: 'Public',
      privacyShareable: 'Shareable',
      privacyPrivate: 'Private',
      shareProfile: 'Share Profile',
      settings: 'Settings',
      language: 'Language',
    },
    onboarding: {
      welcome: 'Welcome to Proof of Learning',
      welcomeDesc: 'Track the moments that changed how you think. Every article, video, or paper that shifted your perspective deserves to be remembered.',
      tagline: 'YOUR COGNITIVE EVOLUTION, DOCUMENTED.',
      step1Title: 'Document Your Shifts',
      step1Desc: 'Record moments when your understanding fundamentally changed.',
      step2Title: 'Share Your Journey',
      step2Desc: 'Make your shifts public to inspire others on their learning path.',
      step3Title: 'Explore the Map',
      step3Desc: 'Discover cognitive shifts happening around the world.',
      step4Title: 'Build Learning Trails',
      step4Desc: 'Curate sequences of shifts to create learning pathways.',
      step5Title: 'Join the Community',
      step5Desc: 'Connect with curious minds documenting their evolution.',
      skip: 'SKIP TUTORIAL',
      getStarted: 'Get Started',
    },
    tooltips: {
      beforeBelief: 'Describe what you thought or believed before encountering this source',
      afterBelief: 'Describe how your understanding changed after engaging with this source',
      reflection: 'Optional: Add any additional thoughts about why this shift was meaningful',
      publicVisibility: 'Your shift will appear in the public feed and on the map',
      privateVisibility: 'Only you can see this shift',
      clustering: 'Group nearby markers together for cleaner visualization',
      fitView: 'Adjust the map to show all markers',
      shareProfile: 'Generate a link to share your public profile',
    },
  },
  es: {
    nav: {
      feed: 'Feed',
      map: 'Mapa',
      myShifts: 'Mis Cambios',
      trails: 'Rutas',
      addShift: 'Añadir Cambio',
      profile: 'Perfil',
      signIn: 'Iniciar Sesión',
      signOut: 'Cerrar Sesión',
      search: 'Buscar...',
      lightMode: 'Modo claro',
      darkMode: 'Modo oscuro',
    },
    common: {
      loading: 'Cargando...',
      error: 'Algo salió mal',
      retry: 'Reintentar',
      save: 'Guardar',
      cancel: 'Cancelar',
      delete: 'Eliminar',
      edit: 'Editar',
      share: 'Compartir',
      close: 'Cerrar',
      back: 'Atrás',
      next: 'Siguiente',
      previous: 'Anterior',
      viewAll: 'Ver Todo',
      learnMore: 'Saber Más',
      copied: '¡Copiado!',
      noResults: 'No se encontraron resultados',
      searchPlaceholder: 'Buscar cambios, rutas...',
    },
    feed: {
      title: 'Feed Global',
      subtitle: 'Descubre cambios cognitivos de estudiantes de todo el mundo',
      emptyState: 'Aún no hay cambios. ¡Sé el primero en documentar tu aprendizaje!',
      filterAll: 'Todos',
      filterArticle: 'Artículos',
      filterVideo: 'Videos',
      filterPaper: 'Artículos',
      filterPodcast: 'Podcasts',
      viewShift: 'Ver Cambio',
      saveShift: 'Guardar',
      views: 'vistas',
      readMore: 'Leer más',
    },
    map: {
      title: 'Mapa de Aprendizaje',
      subtitle: 'Explora los cambios cognitivos globales',
      emptyState: 'Aún no hay cambios con ubicación',
      clustering: 'Agrupación',
      fitView: 'Ajustar vista',
      viewDetails: 'Ver Detalles',
      shiftsInArea: 'cambios en esta área',
      location: 'Ubicación',
    },
    myShifts: {
      title: 'Mis Cambios',
      subtitle: 'Tu evolución cognitiva documentada',
      emptyState: 'Aún no has documentado ningún cambio.',
      emptyAction: 'Crea tu primer cambio',
      public: 'Público',
      private: 'Privado',
      createdAt: 'Creado',
      totalShifts: 'Total de Cambios',
      publicShifts: 'Públicos',
    },
    trails: {
      title: 'Rutas de Aprendizaje',
      subtitle: 'Secuencias curadas de cambios cognitivos',
      emptyState: '¡Aún no hay rutas. Crea tu primer camino de aprendizaje!',
      emptyAction: 'Crear Ruta',
      createTrail: 'Crear Ruta',
      trailName: 'Nombre de la Ruta',
      trailDescription: 'Descripción',
      addShifts: 'Añadir Cambios',
      shifts: 'cambios',
      shareTrail: 'Compartir Ruta',
    },
    create: {
      title: 'Documentar un Cambio',
      subtitle: 'Captura un momento de evolución cognitiva',
      step1: 'Fuente',
      step2: 'Antes',
      step3: 'Después',
      step4: 'Detalles',
      sourceLink: 'Enlace de la Fuente',
      sourceLinkPlaceholder: 'https://...',
      sourceTitle: 'Título de la Fuente',
      sourceTitlePlaceholder: 'Título del artículo, video o documento',
      sourceType: 'Tipo de Fuente',
      beforeBelief: 'Lo Que Creía Antes',
      beforeBeliefPlaceholder: 'Describe tu comprensión antes de esta fuente...',
      afterBelief: 'Lo Que Entiendo Ahora',
      afterBeliefPlaceholder: 'Describe tu nueva comprensión...',
      reflection: 'Reflexión',
      reflectionPlaceholder: '¿Qué hizo significativo este cambio?',
      location: 'Ubicación (opcional)',
      locationPlaceholder: '¿Dónde estabas cuando aprendiste esto?',
      visibility: 'Visibilidad',
      visibilityPublic: 'Público - Compartir con la comunidad',
      visibilityPrivate: 'Privado - Solo visible para ti',
      submit: 'Guardar Cambio',
      submitting: 'Guardando...',
      success: '¡Cambio documentado con éxito!',
    },
    profile: {
      title: 'Perfil',
      subtitle: 'Tu viaje de aprendizaje',
      stats: 'Estadísticas',
      totalShifts: 'Total de Cambios',
      publicShifts: 'Cambios Públicos',
      savedShifts: 'Cambios Guardados',
      totalViews: 'Vistas Totales',
      privacy: 'Configuración de Privacidad',
      privacyPublic: 'Público',
      privacyShareable: 'Compartible',
      privacyPrivate: 'Privado',
      shareProfile: 'Compartir Perfil',
      settings: 'Configuración',
      language: 'Idioma',
    },
    onboarding: {
      welcome: 'Bienvenido a Proof of Learning',
      welcomeDesc: 'Registra los momentos que cambiaron tu forma de pensar. Cada artículo, video o documento que cambió tu perspectiva merece ser recordado.',
      tagline: 'TU EVOLUCIÓN COGNITIVA, DOCUMENTADA.',
      step1Title: 'Documenta Tus Cambios',
      step1Desc: 'Registra momentos en que tu comprensión cambió fundamentalmente.',
      step2Title: 'Comparte Tu Viaje',
      step2Desc: 'Haz públicos tus cambios para inspirar a otros en su camino de aprendizaje.',
      step3Title: 'Explora el Mapa',
      step3Desc: 'Descubre cambios cognitivos ocurriendo en todo el mundo.',
      step4Title: 'Construye Rutas de Aprendizaje',
      step4Desc: 'Cura secuencias de cambios para crear caminos de aprendizaje.',
      step5Title: 'Únete a la Comunidad',
      step5Desc: 'Conecta con mentes curiosas documentando su evolución.',
      skip: 'SALTAR TUTORIAL',
      getStarted: 'Comenzar',
    },
    tooltips: {
      beforeBelief: 'Describe lo que pensabas o creías antes de encontrar esta fuente',
      afterBelief: 'Describe cómo cambió tu comprensión después de interactuar con esta fuente',
      reflection: 'Opcional: Añade pensamientos adicionales sobre por qué este cambio fue significativo',
      publicVisibility: 'Tu cambio aparecerá en el feed público y en el mapa',
      privateVisibility: 'Solo tú puedes ver este cambio',
      clustering: 'Agrupar marcadores cercanos para una visualización más limpia',
      fitView: 'Ajustar el mapa para mostrar todos los marcadores',
      shareProfile: 'Generar un enlace para compartir tu perfil público',
    },
  },
  zh: {
    nav: {
      feed: '动态',
      map: '地图',
      myShifts: '我的转变',
      trails: '学习路径',
      addShift: '添加转变',
      profile: '个人资料',
      signIn: '登录',
      signOut: '退出',
      search: '搜索...',
      lightMode: '浅色模式',
      darkMode: '深色模式',
    },
    common: {
      loading: '加载中...',
      error: '出了点问题',
      retry: '重试',
      save: '保存',
      cancel: '取消',
      delete: '删除',
      edit: '编辑',
      share: '分享',
      close: '关闭',
      back: '返回',
      next: '下一步',
      previous: '上一步',
      viewAll: '查看全部',
      learnMore: '了解更多',
      copied: '已复制！',
      noResults: '未找到结果',
      searchPlaceholder: '搜索转变、路径...',
    },
    feed: {
      title: '全球动态',
      subtitle: '发现来自世界各地学习者的认知转变',
      emptyState: '还没有转变记录。成为第一个记录你学习的人！',
      filterAll: '全部',
      filterArticle: '文章',
      filterVideo: '视频',
      filterPaper: '论文',
      filterPodcast: '播客',
      viewShift: '查看转变',
      saveShift: '保存',
      views: '次浏览',
      readMore: '阅读更多',
    },
    map: {
      title: '学习地图',
      subtitle: '探索全球正在发生的认知转变',
      emptyState: '还没有基于位置的转变',
      clustering: '聚合',
      fitView: '适应视图',
      viewDetails: '查看详情',
      shiftsInArea: '个转变在此区域',
      location: '位置',
    },
    myShifts: {
      title: '我的转变',
      subtitle: '你记录的认知进化',
      emptyState: '你还没有记录任何转变。',
      emptyAction: '创建你的第一个转变',
      public: '公开',
      private: '私密',
      createdAt: '创建于',
      totalShifts: '总转变数',
      publicShifts: '公开',
    },
    trails: {
      title: '学习路径',
      subtitle: '精选的认知转变序列',
      emptyState: '还没有路径。创建你的第一条学习路径！',
      emptyAction: '创建路径',
      createTrail: '创建路径',
      trailName: '路径名称',
      trailDescription: '描述',
      addShifts: '添加转变',
      shifts: '个转变',
      shareTrail: '分享路径',
    },
    create: {
      title: '记录一次转变',
      subtitle: '捕捉认知进化的时刻',
      step1: '来源',
      step2: '之前',
      step3: '之后',
      step4: '详情',
      sourceLink: '来源链接',
      sourceLinkPlaceholder: 'https://...',
      sourceTitle: '来源标题',
      sourceTitlePlaceholder: '文章、视频或论文标题',
      sourceType: '来源类型',
      beforeBelief: '我之前的理解',
      beforeBeliefPlaceholder: '描述你在接触这个来源之前的理解...',
      afterBelief: '我现在的理解',
      afterBeliefPlaceholder: '描述你的新理解...',
      reflection: '反思',
      reflectionPlaceholder: '是什么让这次转变如此重要？',
      location: '位置（可选）',
      locationPlaceholder: '你在哪里学到这些的？',
      visibility: '可见性',
      visibilityPublic: '公开 - 与社区分享',
      visibilityPrivate: '私密 - 仅自己可见',
      submit: '保存转变',
      submitting: '保存中...',
      success: '转变记录成功！',
    },
    profile: {
      title: '个人资料',
      subtitle: '你的学习之旅',
      stats: '统计',
      totalShifts: '总转变数',
      publicShifts: '公开转变',
      savedShifts: '已保存转变',
      totalViews: '总浏览量',
      privacy: '隐私设置',
      privacyPublic: '公开',
      privacyShareable: '可分享',
      privacyPrivate: '私密',
      shareProfile: '分享资料',
      settings: '设置',
      language: '语言',
    },
    onboarding: {
      welcome: '欢迎来到 Proof of Learning',
      welcomeDesc: '追踪那些改变你思维方式的时刻。每一篇文章、视频或论文改变了你的观点，都值得被铭记。',
      tagline: '记录你的认知进化。',
      step1Title: '记录你的转变',
      step1Desc: '记录那些从根本上改变了你理解的时刻。',
      step2Title: '分享你的旅程',
      step2Desc: '公开你的转变，激励他人的学习之路。',
      step3Title: '探索地图',
      step3Desc: '发现世界各地正在发生的认知转变。',
      step4Title: '构建学习路径',
      step4Desc: '策划转变序列，创建学习途径。',
      step5Title: '加入社区',
      step5Desc: '与记录自己进化的好奇心连接。',
      skip: '跳过教程',
      getStarted: '开始使用',
    },
    tooltips: {
      beforeBelief: '描述你在接触这个来源之前的想法或信念',
      afterBelief: '描述在接触这个来源之后你的理解是如何改变的',
      reflection: '可选：添加关于这次转变为何有意义的额外想法',
      publicVisibility: '你的转变将出现在公开动态和地图上',
      privateVisibility: '只有你能看到这个转变',
      clustering: '将附近的标记聚合在一起以获得更清晰的视觉效果',
      fitView: '调整地图以显示所有标记',
      shareProfile: '生成链接以分享你的公开资料',
    },
  },
  fr: {
    nav: {
      feed: 'Flux',
      map: 'Carte',
      myShifts: 'Mes Évolutions',
      trails: 'Parcours',
      addShift: 'Ajouter',
      profile: 'Profil',
      signIn: 'Connexion',
      signOut: 'Déconnexion',
      search: 'Rechercher...',
      lightMode: 'Mode clair',
      darkMode: 'Mode sombre',
    },
    common: {
      loading: 'Chargement...',
      error: 'Une erreur est survenue',
      retry: 'Réessayer',
      save: 'Enregistrer',
      cancel: 'Annuler',
      delete: 'Supprimer',
      edit: 'Modifier',
      share: 'Partager',
      close: 'Fermer',
      back: 'Retour',
      next: 'Suivant',
      previous: 'Précédent',
      viewAll: 'Voir Tout',
      learnMore: 'En Savoir Plus',
      copied: 'Copié !',
      noResults: 'Aucun résultat trouvé',
      searchPlaceholder: 'Rechercher des évolutions, parcours...',
    },
    feed: {
      title: 'Flux Global',
      subtitle: 'Découvrez les évolutions cognitives du monde entier',
      emptyState: 'Pas encore d\'évolutions. Soyez le premier à documenter votre apprentissage !',
      filterAll: 'Tout',
      filterArticle: 'Articles',
      filterVideo: 'Vidéos',
      filterPaper: 'Publications',
      filterPodcast: 'Podcasts',
      viewShift: 'Voir l\'Évolution',
      saveShift: 'Sauvegarder',
      views: 'vues',
      readMore: 'Lire plus',
    },
    map: {
      title: 'Carte d\'Apprentissage',
      subtitle: 'Explorez les évolutions cognitives mondiales',
      emptyState: 'Pas encore d\'évolutions géolocalisées',
      clustering: 'Regroupement',
      fitView: 'Ajuster la vue',
      viewDetails: 'Voir les Détails',
      shiftsInArea: 'évolutions dans cette zone',
      location: 'Emplacement',
    },
    myShifts: {
      title: 'Mes Évolutions',
      subtitle: 'Votre évolution cognitive documentée',
      emptyState: 'Vous n\'avez pas encore documenté d\'évolution.',
      emptyAction: 'Créez votre première évolution',
      public: 'Public',
      private: 'Privé',
      createdAt: 'Créé le',
      totalShifts: 'Total des Évolutions',
      publicShifts: 'Publiques',
    },
    trails: {
      title: 'Parcours d\'Apprentissage',
      subtitle: 'Séquences organisées d\'évolutions cognitives',
      emptyState: 'Pas encore de parcours. Créez votre premier chemin d\'apprentissage !',
      emptyAction: 'Créer un Parcours',
      createTrail: 'Créer un Parcours',
      trailName: 'Nom du Parcours',
      trailDescription: 'Description',
      addShifts: 'Ajouter des Évolutions',
      shifts: 'évolutions',
      shareTrail: 'Partager le Parcours',
    },
    create: {
      title: 'Documenter une Évolution',
      subtitle: 'Capturez un moment d\'évolution cognitive',
      step1: 'Source',
      step2: 'Avant',
      step3: 'Après',
      step4: 'Détails',
      sourceLink: 'Lien Source',
      sourceLinkPlaceholder: 'https://...',
      sourceTitle: 'Titre de la Source',
      sourceTitlePlaceholder: 'Titre de l\'article, vidéo ou publication',
      sourceType: 'Type de Source',
      beforeBelief: 'Ce Que Je Croyais Avant',
      beforeBeliefPlaceholder: 'Décrivez votre compréhension avant cette source...',
      afterBelief: 'Ce Que Je Comprends Maintenant',
      afterBeliefPlaceholder: 'Décrivez votre nouvelle compréhension...',
      reflection: 'Réflexion',
      reflectionPlaceholder: 'Qu\'est-ce qui a rendu cette évolution significative ?',
      location: 'Lieu (optionnel)',
      locationPlaceholder: 'Où étiez-vous quand vous avez appris cela ?',
      visibility: 'Visibilité',
      visibilityPublic: 'Public - Partager avec la communauté',
      visibilityPrivate: 'Privé - Visible uniquement par vous',
      submit: 'Enregistrer l\'Évolution',
      submitting: 'Enregistrement...',
      success: 'Évolution documentée avec succès !',
    },
    profile: {
      title: 'Profil',
      subtitle: 'Votre parcours d\'apprentissage',
      stats: 'Statistiques',
      totalShifts: 'Total des Évolutions',
      publicShifts: 'Évolutions Publiques',
      savedShifts: 'Évolutions Sauvegardées',
      totalViews: 'Vues Totales',
      privacy: 'Paramètres de Confidentialité',
      privacyPublic: 'Public',
      privacyShareable: 'Partageable',
      privacyPrivate: 'Privé',
      shareProfile: 'Partager le Profil',
      settings: 'Paramètres',
      language: 'Langue',
    },
    onboarding: {
      welcome: 'Bienvenue sur Proof of Learning',
      welcomeDesc: 'Suivez les moments qui ont changé votre façon de penser. Chaque article, vidéo ou publication qui a changé votre perspective mérite d\'être mémorisé.',
      tagline: 'VOTRE ÉVOLUTION COGNITIVE, DOCUMENTÉE.',
      step1Title: 'Documentez Vos Évolutions',
      step1Desc: 'Enregistrez les moments où votre compréhension a fondamentalement changé.',
      step2Title: 'Partagez Votre Parcours',
      step2Desc: 'Rendez vos évolutions publiques pour inspirer les autres.',
      step3Title: 'Explorez la Carte',
      step3Desc: 'Découvrez les évolutions cognitives dans le monde entier.',
      step4Title: 'Créez des Parcours d\'Apprentissage',
      step4Desc: 'Organisez des séquences d\'évolutions pour créer des chemins d\'apprentissage.',
      step5Title: 'Rejoignez la Communauté',
      step5Desc: 'Connectez-vous avec des esprits curieux documentant leur évolution.',
      skip: 'PASSER LE TUTORIEL',
      getStarted: 'Commencer',
    },
    tooltips: {
      beforeBelief: 'Décrivez ce que vous pensiez ou croyiez avant de découvrir cette source',
      afterBelief: 'Décrivez comment votre compréhension a changé après avoir interagi avec cette source',
      reflection: 'Optionnel : Ajoutez des réflexions supplémentaires sur l\'importance de cette évolution',
      publicVisibility: 'Votre évolution apparaîtra dans le flux public et sur la carte',
      privateVisibility: 'Seul vous pouvez voir cette évolution',
      clustering: 'Regrouper les marqueurs proches pour une visualisation plus claire',
      fitView: 'Ajuster la carte pour afficher tous les marqueurs',
      shareProfile: 'Générer un lien pour partager votre profil public',
    },
  },
  de: {
    nav: {
      feed: 'Feed',
      map: 'Karte',
      myShifts: 'Meine Wandlungen',
      trails: 'Pfade',
      addShift: 'Hinzufügen',
      profile: 'Profil',
      signIn: 'Anmelden',
      signOut: 'Abmelden',
      search: 'Suchen...',
      lightMode: 'Heller Modus',
      darkMode: 'Dunkler Modus',
    },
    common: {
      loading: 'Wird geladen...',
      error: 'Etwas ist schiefgelaufen',
      retry: 'Erneut versuchen',
      save: 'Speichern',
      cancel: 'Abbrechen',
      delete: 'Löschen',
      edit: 'Bearbeiten',
      share: 'Teilen',
      close: 'Schließen',
      back: 'Zurück',
      next: 'Weiter',
      previous: 'Zurück',
      viewAll: 'Alle Anzeigen',
      learnMore: 'Mehr Erfahren',
      copied: 'Kopiert!',
      noResults: 'Keine Ergebnisse gefunden',
      searchPlaceholder: 'Wandlungen, Pfade suchen...',
    },
    feed: {
      title: 'Globaler Feed',
      subtitle: 'Entdecken Sie kognitive Wandlungen von Lernenden weltweit',
      emptyState: 'Noch keine Wandlungen. Seien Sie der Erste, der Ihr Lernen dokumentiert!',
      filterAll: 'Alle',
      filterArticle: 'Artikel',
      filterVideo: 'Videos',
      filterPaper: 'Publikationen',
      filterPodcast: 'Podcasts',
      viewShift: 'Wandlung Ansehen',
      saveShift: 'Speichern',
      views: 'Aufrufe',
      readMore: 'Mehr lesen',
    },
    map: {
      title: 'Lernkarte',
      subtitle: 'Erkunden Sie globale kognitive Wandlungen',
      emptyState: 'Noch keine ortsbezogenen Wandlungen',
      clustering: 'Gruppierung',
      fitView: 'Alle anzeigen',
      viewDetails: 'Details Ansehen',
      shiftsInArea: 'Wandlungen in diesem Bereich',
      location: 'Standort',
    },
    myShifts: {
      title: 'Meine Wandlungen',
      subtitle: 'Ihre dokumentierte kognitive Evolution',
      emptyState: 'Sie haben noch keine Wandlungen dokumentiert.',
      emptyAction: 'Erstellen Sie Ihre erste Wandlung',
      public: 'Öffentlich',
      private: 'Privat',
      createdAt: 'Erstellt am',
      totalShifts: 'Gesamt Wandlungen',
      publicShifts: 'Öffentlich',
    },
    trails: {
      title: 'Lernpfade',
      subtitle: 'Kuratierte Sequenzen kognitiver Wandlungen',
      emptyState: 'Noch keine Pfade. Erstellen Sie Ihren ersten Lernweg!',
      emptyAction: 'Pfad Erstellen',
      createTrail: 'Pfad Erstellen',
      trailName: 'Pfadname',
      trailDescription: 'Beschreibung',
      addShifts: 'Wandlungen Hinzufügen',
      shifts: 'Wandlungen',
      shareTrail: 'Pfad Teilen',
    },
    create: {
      title: 'Eine Wandlung Dokumentieren',
      subtitle: 'Erfassen Sie einen Moment kognitiver Evolution',
      step1: 'Quelle',
      step2: 'Vorher',
      step3: 'Nachher',
      step4: 'Details',
      sourceLink: 'Quelllink',
      sourceLinkPlaceholder: 'https://...',
      sourceTitle: 'Quelltitel',
      sourceTitlePlaceholder: 'Titel des Artikels, Videos oder der Publikation',
      sourceType: 'Quelltyp',
      beforeBelief: 'Was Ich Vorher Glaubte',
      beforeBeliefPlaceholder: 'Beschreiben Sie Ihr Verständnis vor dieser Quelle...',
      afterBelief: 'Was Ich Jetzt Verstehe',
      afterBeliefPlaceholder: 'Beschreiben Sie Ihr neues Verständnis...',
      reflection: 'Reflexion',
      reflectionPlaceholder: 'Was machte diese Wandlung bedeutsam?',
      location: 'Standort (optional)',
      locationPlaceholder: 'Wo waren Sie, als Sie dies lernten?',
      visibility: 'Sichtbarkeit',
      visibilityPublic: 'Öffentlich - Mit der Community teilen',
      visibilityPrivate: 'Privat - Nur für Sie sichtbar',
      submit: 'Wandlung Speichern',
      submitting: 'Wird gespeichert...',
      success: 'Wandlung erfolgreich dokumentiert!',
    },
    profile: {
      title: 'Profil',
      subtitle: 'Ihre Lernreise',
      stats: 'Statistiken',
      totalShifts: 'Gesamt Wandlungen',
      publicShifts: 'Öffentliche Wandlungen',
      savedShifts: 'Gespeicherte Wandlungen',
      totalViews: 'Gesamtaufrufe',
      privacy: 'Datenschutzeinstellungen',
      privacyPublic: 'Öffentlich',
      privacyShareable: 'Teilbar',
      privacyPrivate: 'Privat',
      shareProfile: 'Profil Teilen',
      settings: 'Einstellungen',
      language: 'Sprache',
    },
    onboarding: {
      welcome: 'Willkommen bei Proof of Learning',
      welcomeDesc: 'Verfolgen Sie die Momente, die Ihre Denkweise verändert haben. Jeder Artikel, jedes Video oder jede Publikation, die Ihre Perspektive verändert hat, verdient es, erinnert zu werden.',
      tagline: 'IHRE KOGNITIVE EVOLUTION, DOKUMENTIERT.',
      step1Title: 'Dokumentieren Sie Ihre Wandlungen',
      step1Desc: 'Erfassen Sie Momente, in denen sich Ihr Verständnis grundlegend verändert hat.',
      step2Title: 'Teilen Sie Ihre Reise',
      step2Desc: 'Machen Sie Ihre Wandlungen öffentlich, um andere auf ihrem Lernweg zu inspirieren.',
      step3Title: 'Erkunden Sie die Karte',
      step3Desc: 'Entdecken Sie kognitive Wandlungen, die weltweit stattfinden.',
      step4Title: 'Erstellen Sie Lernpfade',
      step4Desc: 'Kuratieren Sie Sequenzen von Wandlungen, um Lernwege zu erstellen.',
      step5Title: 'Treten Sie der Community Bei',
      step5Desc: 'Verbinden Sie sich mit neugierigen Köpfen, die ihre Evolution dokumentieren.',
      skip: 'TUTORIAL ÜBERSPRINGEN',
      getStarted: 'Loslegen',
    },
    tooltips: {
      beforeBelief: 'Beschreiben Sie, was Sie dachten oder glaubten, bevor Sie diese Quelle entdeckten',
      afterBelief: 'Beschreiben Sie, wie sich Ihr Verständnis nach der Auseinandersetzung mit dieser Quelle verändert hat',
      reflection: 'Optional: Fügen Sie zusätzliche Gedanken darüber hinzu, warum diese Wandlung bedeutsam war',
      publicVisibility: 'Ihre Wandlung erscheint im öffentlichen Feed und auf der Karte',
      privateVisibility: 'Nur Sie können diese Wandlung sehen',
      clustering: 'Nahe gelegene Marker für eine klarere Visualisierung gruppieren',
      fitView: 'Die Karte anpassen, um alle Marker anzuzeigen',
      shareProfile: 'Einen Link generieren, um Ihr öffentliches Profil zu teilen',
    },
  },
};

export const languageNames: Record<Language, string> = {
  en: 'English',
  es: 'Español',
  zh: '中文',
  fr: 'Français',
  de: 'Deutsch',
};
